/* eslint-disable react/prop-types */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, Send, X, Bot, User as UserIcon, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";

const QUICK_PROMPTS = [
  "Improve my summary",
  "Rewrite all experience bullets stronger",
  "Add missing skills based on my experience",
  "Make this ATS optimized",
  "Sharpen my key highlights",
];

export default function AIAssistantDrawer({ open, onClose, resumeId, currentData, onPatch }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: "assistant",
        text: "Hi! I'm your AI resume copilot. Ask me to improve any section, rewrite bullets, suggest skills, or make the resume ATS-friendly. I'll apply changes directly to the live preview.",
      }]);
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (msg) => {
    const text = (msg ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const history = messages.slice(-6).map((m) => ({ role: m.role, text: m.text }));
      const r = await api.post(`/resumes/${resumeId}/chat`, { message: text, history });
      const reply = r.data?.reply || "Done.";
      const patch = r.data?.patch;
      setMessages((m) => [...m, { role: "assistant", text: reply, applied: !!patch }]);
      if (patch && onPatch) {
        onPatch(patch);
        toast.success("AI applied changes");
      }
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry — that request failed. Please try again." }]);
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed top-0 right-0 h-full w-full sm:w-[420px] z-40 bg-card border-l border-border shadow-2xl flex flex-col" data-testid="ai-chat-drawer">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="size-8 rounded-md bg-primary text-primary-foreground grid place-items-center"><Sparkles className="size-4" /></div>
          <div>
            <div className="display font-bold text-sm leading-none">AI Copilot</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">GPT-5.2 · context-aware</div>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} data-testid="ai-chat-close"><X className="size-4" /></Button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <div className={`size-7 rounded-md grid place-items-center shrink-0 ${m.role === "user" ? "bg-secondary" : "bg-primary text-primary-foreground"}`}>
              {m.role === "user" ? <UserIcon className="size-3.5" /> : <Bot className="size-3.5" />}
            </div>
            <div className={`text-sm max-w-[85%] rounded-md px-3 py-2 ${m.role === "user" ? "bg-secondary" : "bg-accent text-accent-foreground"}`}>
              <div>{m.text}</div>
              {m.applied && <div className="text-[10px] mt-1 inline-flex items-center gap-1 font-semibold uppercase tracking-[0.18em] opacity-80"><Wand2 className="size-3" /> Applied</div>}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex gap-2">
            <div className="size-7 rounded-md grid place-items-center bg-primary text-primary-foreground"><Bot className="size-3.5" /></div>
            <div className="text-sm rounded-md px-3 py-2 bg-accent text-accent-foreground inline-flex items-center gap-2"><Loader2 className="size-3.5 animate-spin" /> Thinking…</div>
          </div>
        )}
      </div>

      {messages.length <= 1 && (
        <div className="px-4 pb-2 flex flex-wrap gap-1.5">
          {QUICK_PROMPTS.map((p) => (
            <button key={p} onClick={() => send(p)} className="chip lift" data-testid={`ai-quick-${p.slice(0, 10)}`}>{p}</button>
          ))}
        </div>
      )}

      <div className="p-3 border-t border-border">
        <div className="flex gap-2">
          <Textarea rows={2} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Ask the AI to edit anything…" className="resize-none" data-testid="ai-chat-input" />
          <Button onClick={() => send()} disabled={busy || !input.trim()} className="self-end lift" data-testid="ai-chat-send"><Send className="size-4" /></Button>
        </div>
      </div>
    </div>
  );
}
